import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { PingLatencyPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/ping-monitoring')({
  component: PingMonitoringPage,
  head: () => ({
    meta: [
      { title: 'Ping Monitoring — ICMP Latency Tracking | Uptrack' },
      { name: 'description', content: 'Monitor servers with ICMP ping. Track latency, detect packet loss, and get alerted when hosts go unreachable. Included with every paid plan.' },
      { property: 'og:title', content: 'Ping Monitoring — ICMP Latency Tracking | Uptrack' },
      { property: 'og:description', content: 'ICMP ping monitoring with latency tracking and packet loss detection. Included with every paid plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-ping-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/ping-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Ping Monitoring', url: 'https://uptrack.app/features/ping-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Ping Monitoring',
        description: 'ICMP ping monitoring with latency tracking and packet loss detection for servers and network devices.',
        url: 'https://uptrack.app/features/ping-monitoring',
      })),
    ],
  }),
})

function PingMonitoringPage() {
  return (
    <FeaturePage
      section="ICMP ping monitoring"
      title="Ping monitoring with latency tracking."
      intro="Monitor server reachability with ICMP ping. Track round-trip latency over time, detect packet loss, and get alerted when hosts become unreachable."
      featuresHeading="Network-level visibility"
      features={[
        { title: 'ICMP ping checks', desc: 'The most fundamental reachability test. If ping fails, the host is down or the network path is broken.' },
        { title: 'Latency tracking', desc: 'Track round-trip time over hours, days, and weeks. Spot network degradation trends before they escalate.' },
        { title: 'Packet loss detection', desc: 'Intermittent packet loss degrades performance silently. Uptrack detects and reports loss patterns.' },
        { title: 'Historical charts', desc: 'Visualize latency and availability over time. Correlate network issues with application-level problems.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Most tools offer basic ping but skip packet loss — the metric that actually matters for network health.',
        columns: ['Provider', 'Ping Monitor', 'Latency Tracking', 'Packet Loss'],
        rows: [
          { cells: ['UptimeRobot', 'Yes', 'Basic', 'No'] },
          { cells: ['Better Stack', 'Yes', 'Yes', 'No'] },
          { cells: ['StatusCake', 'Paid', 'Yes', 'No'] },
          { cells: ['Uptrack', 'All paid plans', 'Yes', 'Yes'], highlight: true },
        ],
      }}
      why={{ heading: 'Why ping monitoring matters', body: 'HTTP checks tell you if an application responds. Ping tells you if the host is reachable at all. When your server goes offline at the network level, ping monitoring catches it immediately — no waiting for HTTP timeouts. It is the fastest signal for host-level failures.' }}
      cta={{ heading: 'Start pinging your servers now', body: 'Every paid plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <PingLatencyPiece />
        </ProductFrame>
      }
    />
  )
}
